import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState
} from "react";
import api from "../../api";
import { useIntercom } from "react-use-intercom";
import { Customer, AuthState, Context } from "./types";
import { postEvent } from "../../helpers/postEvent";
import { usePostHog } from "posthog-js/react";
import { config } from "config";

// Used to define successful auth state
// do not use this function if authWithLink or getCurrentCustomer failed
const getPermissionAuthState = ({
  customer,
  ignorePassword
}: {
  customer: Customer;
  ignorePassword?: boolean;
}) => {
  const phoneVerified = customer.phone_verified;
  const passwordVerified = customer.onboarded || ignorePassword;
  const enrolled = customer.applied_for_enrollment;

  if (phoneVerified && passwordVerified && enrolled) return AuthState.FULL;
  if (phoneVerified && passwordVerified) return AuthState.ONBOARDING_REQUIRED;
  if (phoneVerified) return AuthState.PASSWORD_REQUIRED;
  return AuthState.PHONE_VERIFICATION_REQUIRED;
};

const AuthContext = createContext<Context>({
  authState: AuthState.LOADING,
  logOut: () => Promise.resolve(),
  refreshAuth: () => Promise.resolve(),
  sdkConfig: {}
});

export function AuthProvider({
  token,
  children,
  hasClose,
  bypassPlaidAccountConfirmation,
  withIntercom
}: {
  token?: string;
  children: ReactNode;
  hasClose: boolean;
  bypassPlaidAccountConfirmation: boolean;
  withIntercom?: boolean;
}) {
  const posthog = usePostHog();
  const { boot } = useIntercom();

  const [customer, setCustomer] = useState<Customer>();
  const [authState, setAuthState] = useState<AuthState>(AuthState.LOADING);
  const [error, setError] = useState<string>("");

  const partner = customer?.partner;

  const refreshAuth = (data?: Customer) => {
    setAuthState(AuthState.LOADING);

    const authenticate = () =>
      (token
        ? api.authWithLink({ token })
        : data
          ? Promise.resolve({ data })
          : api.getCurrentCustomer()
      )
        .then(({ data: pCustomer }) => {
          setCustomer(pCustomer);
          const aState = getPermissionAuthState({
            customer: pCustomer,
            ignorePassword: !!token
          });

          setAuthState(aState);
          setError("");

          posthog.reset();
          posthog.setPersonPropertiesForFlags({
            env: config.appEnv
          });
          posthog.identify?.(String(pCustomer.id), {
            name: pCustomer.partner?.name || "no_partner_name",
            email: pCustomer.email,
            phone: pCustomer.phone,
            partner_id: pCustomer.partner?.id || "no_partner_id",
            env: config.appEnv
          });
          boot({
            userId: `${pCustomer.id}`,
            customAttributes: {
              source: pCustomer.partner?.name || "web_enrollment"
            },
            hideDefaultLauncher: true
          });
        })
        .catch((err) => {
          if (token) {
            const errorMessage =
              err?.response?.data?.error?.status === 401
                ? "Unable to authenticate user token"
                : "Oops, seems like something went wrong. Please try again later.";
            setAuthState(AuthState.INTEGRATION_ERROR);
            postEvent({
              type: "action",
              action: "auth-error",
              message: errorMessage
            });
            setError(errorMessage);
          } else setAuthState(AuthState.UNAUTHED);
          setCustomer(undefined);
        });
    return api.logout().then(authenticate).catch(authenticate);
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        customer,
        partner,
        authState,
        error,
        logOut: () =>
          api.signOut().then(() => {
            window?.localStorage?.removeItem("token");
            api.refreshInstance();
            return refreshAuth();
          }),
        token,
        refreshAuth,
        sdkConfig: {
          hasClose,
          bypassPlaidAccountConfirmation,
          withIntercom
        }
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- okay
export const useAuth = () => useContext(AuthContext);
